import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { getDisplayPayload } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/ReturnInstructions/ReturnInstructionsConfiguration/helpers'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { transformResponseReturnInstructions } from './transformers'

const useReturnInstructionSets = ({ storeId, setToastInfo }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const searchRef = useRef('')
  const currentPageRef = useRef(1)
  const [accumulatedResults, setAccumulatedResults] = useState([])
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  const {
    data,
    loading: fetchingReturnInstructions,
    fetchData,
    resetData,
  } = useFetch(configurationServices.getReturnInstructionSets, {
    doInitialRequest: !!storeId,
    params: { storeId, page: 1 },
    transformResponse: transformResponseReturnInstructions,
  })

  const { fetchData: reorderInstructions, loading: savingOrder } = useFetch(
    configurationServices.reorderReturnInstructionSets,
    { doInitialRequest: false }
  )

  useEffect(() => {
    if (data?.results) {
      if (currentPageRef.current === 1) {
        setAccumulatedResults(data.results)
      } else {
        setAccumulatedResults(prev => [...prev, ...data.results])
      }
    }
  }, [data])

  const getParams = page => {
    const params = { storeId, page }
    if (searchRef.current) params.search = searchRef.current

    return params
  }

  const setReturnInstructions = updater => {
    setAccumulatedResults(prev => updater(prev))
    setHasLocalChanges(true)
  }

  const loadMore = () => {
    const nextPage = currentPageRef.current + 1
    currentPageRef.current = nextPage
    fetchData(getParams(nextPage))
  }

  const onSearch = query => {
    searchRef.current = query
    currentPageRef.current = 1
    setHasLocalChanges(false)
    fetchData(getParams(1))
  }

  const refetch = () => {
    currentPageRef.current = 1
    setHasLocalChanges(false)
    fetchData(getParams(1))
  }

  const onSaveOrder = async () => {
    try {
      await reorderInstructions({ storeId, payload: getDisplayPayload(accumulatedResults) })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      refetch()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const navigateToDetail = (instruction, id) => {
    navigate(routes.configurationPage.logistics.returnInstructionDetailConfiguration.replace(':id', id), {
      state: {
        childPageTitle: instruction.name,
        selectedInstruction: instruction,
        previousLocation: location,
      },
    })
  }

  const onEdit = instruction => {
    navigateToDetail(instruction, instruction.id)
  }

  const onDuplicate = instruction => {
    navigateToDetail(instruction, 'duplicate')
  }

  const onCreate = () => {
    navigate(routes.configurationPage.logistics.returnInstructionDetailConfiguration.replace(':id', 'new'), {
      state: { previousLocation: location },
    })
  }

  return {
    returnInstructions: accumulatedResults,
    setReturnInstructions,
    fetchingReturnInstructions,
    revertChanges: () => {
      resetData()
      refetch()
    },
    refetchReturnInstructions: refetch,
    hasLocalChanges,
    onSearch,
    loadMore,
    hasNextPage: !!data?.next,
    totalCount: data?.count || 0,
    onSaveOrder,
    savingOrder,
    onEdit,
    onDuplicate,
    onCreate,
  }
}

export default useReturnInstructionSets
